import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { PersonCircle, List as ListIcon, X as XIcon } from 'react-bootstrap-icons';
import { FaRobot } from 'react-icons/fa';
import { logout } from '../../utils/api';
import { getClientDashboardCached } from '../../utils/metadataCache';
import { logoutUser } from '../../redux/actions';
import { resolveAssetUrl } from '../../utils/common';
import ImpersonationBanner from './ImpersonationBanner';
import GlobalSearch from '../../components/GlobalSearch';
import SavvyBot from '../../components/SavvyBot';
import { useScreenshotMode } from '../../context/ScreenshotModeContext';
import './index.scss';

const NAV_ITEMS = [
  { to: '/client/dashboard', label: 'Dashboard' },
  { to: '/client/ledger', label: 'Billing' },
  { to: '/client/inventory', label: 'Inventory', matchPrefix: '/client/inventory' },
  { to: '/client/orders', label: 'Orders' },
  { to: '/client/invoices', label: 'Invoices' },
  { to: '/client/purchases', label: 'Purchases' },
  {
    to: '/client/shipments',
    label: 'Shipments',
    matchPrefix: ['/client/shipments', '/client/asn', '/client/outbound'],
  },
  { to: '/client/transfers', label: 'Transfers', matchPrefix: '/client/transfers' },
  { to: '/client/help', label: 'Help', matchPrefix: '/client/help' },
];

function ClientLayout() {
  const user = useSelector((state) => state.user);
  // Scope the cached logo to the active client context so switching
  // impersonated clients (or accounts) never shows a stale store's logo.
  const logoCacheKey = `skusavvy_store_logo:${
    user?.impersonatedClientId ?? user?.clientId ?? user?.email ?? ''
  }`;
  const [storeLogo, setStoreLogo] = useState(() => {
    try {
      return sessionStorage.getItem(logoCacheKey) || null;
    } catch (e) {
      return null;
    }
  });
  const ownerLogoUrl = resolveAssetUrl(
    useSelector(
      (state) =>
        state.user?.impersonatedOwnerLogoUrl ??
        state.user?.store?.logoUrl ??
        state.user?.ownerLogoUrl ??
        null,
    ) ?? storeLogo,
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    enabled: screenshotMode,
    isSuperAdmin,
    toggle: toggleScreenshotMode,
  } = useScreenshotMode();

  // Logos are now served from /api/logos/... (absolute URLs, Task #686).
  // Guard against any stale/unexpected image load failure just in case.
  const [logoFailed, setLogoFailed] = useState(false);

  // Give each new logo identity one render attempt: reset the failure flag
  // whenever the resolved URL changes (impersonated-client switch, a new
  // logo arriving in redux, or the cached URL being replaced).
  useEffect(() => {
    setLogoFailed(false);
  }, [ownerLogoUrl]);

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [sbOpen, setSbOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Fetch the 3PL owner's logo for the portal header (dashboard response
  // carries storeLogoUrl as an absolute /api/logos/... URL, Task #686).
  // Cached per browser session to avoid refetching.
  useEffect(() => {
    let cached = null;
    try {
      cached = sessionStorage.getItem(logoCacheKey) || null;
    } catch (e) {}
    // Evict stale cached values that still point to the old /uploads/logos/
    // path — those are dead and would produce a broken image.
    if (cached && cached.includes('/uploads/logos/')) {
      try {
        sessionStorage.removeItem(logoCacheKey);
      } catch (e) {}
      cached = null;
    }
    if (cached) {
      setStoreLogo(cached);
      return undefined;
    }
    setStoreLogo(null);
    let cancelled = false;
    getClientDashboardCached(logoCacheKey).then((res) => {
      const url = res?.dashboard?.storeLogoUrl ?? null;
      // resolveAssetUrl returns null for dead /uploads/logos/ paths.
      const resolvedUrl = resolveAssetUrl(url);
      if (cancelled || !resolvedUrl) return;
      setStoreLogo(resolvedUrl);
      try {
        sessionStorage.setItem(logoCacheKey, resolvedUrl);
      } catch (e) {}
    });
    return () => {
      cancelled = true;
    };
  }, [logoCacheKey]);

  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Lock body scroll while drawer open
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [drawerOpen]);

  const handleLogout = async () => {
    setUserMenuOpen(false);
    await logout();
    dispatch(logoutUser());
    navigate('/stores');
  };

  const displayName =
    user.firstName || user.lastName
      ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
      : (user.email ?? 'Client');

  const navClass =
    (item) =>
    ({ isActive }) => {
      const prefixes = Array.isArray(item.matchPrefix)
        ? item.matchPrefix
        : item.matchPrefix
          ? [item.matchPrefix]
          : [];
      return isActive || prefixes.some((p) => location.pathname.startsWith(p)) ? 'active' : '';
    };

  return (
    <div className="client-portal">
      <div className="client-portal__sticky-header">
        <ImpersonationBanner />
        <header className="client-portal__header">
          <button
            type="button"
            className="client-portal__menu-btn"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open navigation menu"
            aria-expanded={drawerOpen}>
            <ListIcon size={22} />
          </button>
          <div className="client-portal__brand">
            <a href="https://app.skusavvy.com/company" target="_blank" rel="noopener noreferrer">
              <img
                src="/skusavvy-cube-icon.png"
                alt="SKUSavvy"
                className="client-portal__logo client-portal__logo--skusavvy"
              />
            </a>
            {ownerLogoUrl && !logoFailed && (
              <>
                <span className="client-portal__brand-pipe">|</span>
                <img
                  src={ownerLogoUrl}
                  alt="Company logo"
                  className="client-portal__logo client-portal__logo--owner"
                  onError={() => {
                    setLogoFailed(true);
                    // Drop cached URL so the next session re-fetches.
                    try {
                      sessionStorage.removeItem(logoCacheKey);
                    } catch (e) {}
                  }}
                />
              </>
            )}
          </div>
          <nav className="client-portal__nav">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={navClass(item)}
                end={!item.matchPrefix}>
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="client-portal__actions">
            <div className="client-portal__search">
              <button
                className="cp-savvybot-btn"
                onClick={() => setSbOpen((v) => !v)}
                aria-label="Open SavvyBot 3PL"
                title="SavvyBot 3PL">
                <FaRobot size={18} />
              </button>
              <GlobalSearch portal="client" />
            </div>
            <div className="client-portal__user-menu" ref={userMenuRef}>
              <button
                className="client-portal__user-btn"
                onClick={() => setUserMenuOpen((v) => !v)}
                aria-label="Account menu"
                aria-expanded={userMenuOpen}>
                <PersonCircle size={20} />
              </button>
              {userMenuOpen && (
                <div className="client-portal__user-dropdown">
                  <div className="client-portal__user-dropdown-name">{displayName}</div>
                  <div className="client-portal__user-dropdown-divider" />
                  {isSuperAdmin && (
                    <>
                      <button
                        type="button"
                        data-screenshot-mode-control
                        className="client-portal__user-dropdown-item"
                        onClick={toggleScreenshotMode}
                        aria-pressed={screenshotMode}>
                        {screenshotMode ? 'Use real display data' : 'Randomize screenshot data'}
                      </button>
                      <div className="client-portal__user-dropdown-divider" />
                    </>
                  )}
                  <button
                    className="client-portal__user-dropdown-item"
                    onClick={() => {
                      setUserMenuOpen(false);
                      navigate('/client/account');
                    }}>
                    Account Settings
                  </button>
                  <div className="client-portal__user-dropdown-divider" />
                  <button className="client-portal__user-dropdown-logout" onClick={handleLogout}>
                    Log Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
      </div>

      {drawerOpen && (
        <div
          className="client-portal__drawer-overlay"
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
      )}
      <aside
        className={`client-portal__drawer${drawerOpen ? ' is-open' : ''}`}
        aria-hidden={!drawerOpen}>
        <div className="client-portal__drawer-header">
          <span className="client-portal__drawer-title">Menu</span>
          <button
            type="button"
            className="client-portal__drawer-close"
            onClick={() => setDrawerOpen(false)}
            aria-label="Close navigation menu">
            <XIcon size={22} />
          </button>
        </div>
        <nav className="client-portal__drawer-nav">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={navClass(item)}
              end={!item.matchPrefix}
              onClick={() => setDrawerOpen(false)}>
              {item.label}
            </NavLink>
          ))}
        </nav>
      </aside>

      <main className="client-portal__main">
        <Outlet />
      </main>
      <SavvyBot portal="client" open={sbOpen} onToggle={() => setSbOpen((v) => !v)} />
    </div>
  );
}

export default ClientLayout;
